import cn from 'classnames';

import IconSimpleArrow from '@/assets/icons/icon-simple-arrow.svg';

import styles from './pagination.module.scss';

const {
    paginationWrapper,
    paginationButtonsWrapper,
    paginationButtonStyles,
    leftArrowButtonStyles,
    rightArrowButtonStyles,
    activeButtonStyles,
    mobilePagePositionIndicatorStyles,
    ellipsisStyles,
    leftArrowNoHoverStyles,
    rightArrowNoHoverStyles,
} = styles;

export interface PaginationProps {
    totalPages: number;
    selectedPage: number;
    onChangePage: (value: number) => void;
    isMobileDevice?: boolean;
}

export function Pagination({ totalPages, selectedPage, onChangePage, isMobileDevice }: PaginationProps) {
    return (
        <div className={paginationWrapper}>
            <button
                onClick={() => {
                    onChangePage(selectedPage !== 0 ? selectedPage - 1 : 0);
                }}
                type="button"
                disabled={selectedPage === 0}
                className={isMobileDevice ? leftArrowNoHoverStyles : leftArrowButtonStyles}
                data-tracklinktext="pagination arrow left"
                aria-label="Previous page"
            >
                <IconSimpleArrow aria-hidden="true" focusable="false" />
            </button>
            <span className={mobilePagePositionIndicatorStyles}>{`${selectedPage + 1} / ${totalPages}`}</span>
            <div className={paginationButtonsWrapper}>
                {Array.from({ length: totalPages }).map((_, index) => {
                    const paginationButtonWrapper = cn(paginationButtonStyles, {
                        [activeButtonStyles]: index === selectedPage,
                    });
                    if (index < 5 && selectedPage < 5) {
                        // Show the first 5 page Buttons
                        return (
                            <button
                                key={index}
                                className={paginationButtonWrapper}
                                onClick={() => {
                                    onChangePage(index);
                                }}
                                type="button"
                                data-tracklinktext={`pagination page ${index + 1} clicked`}
                            >{`${index + 1}`}</button>
                        );
                    } else if (index < 2 && selectedPage >= 5) {
                        // Show just the first 2 page buttons
                        return (
                            <button
                                key={index}
                                className={paginationButtonWrapper}
                                onClick={() => {
                                    onChangePage(index);
                                }}
                                type="button"
                                data-tracklinktext={`pagination page ${index + 1} clicked`}
                            >{`${index + 1}`}</button>
                        );
                    }
                    if (index === 2 && selectedPage >= 4) {
                        // Show the first ellipsis after the first two page buttons
                        return (
                            <span key={index} className={ellipsisStyles} aria-hidden="true">
                                <p>...</p>
                            </span>
                        );
                    }
                    if (index <= selectedPage + 1 && index > selectedPage - 2 && selectedPage < totalPages - 4) {
                        // Shows the middle part of the pagination, the actual page and the one before and after
                        return (
                            <button
                                key={index}
                                className={paginationButtonWrapper}
                                onClick={() => {
                                    onChangePage(index);
                                }}
                                type="button"
                                data-tracklinktext={`pagination page ${index + 1} clicked`}
                            >{`${index + 1}`}</button>
                        );
                    }
                    if (index === totalPages - 3 && selectedPage < totalPages - 4) {
                        // Shows the second ellipsis
                        return (
                            <span key={index} className={ellipsisStyles} aria-hidden="true">
                                <p>...</p>
                            </span>
                        );
                    }
                    if (selectedPage > totalPages - 6 && index > totalPages - 6) {
                        // Shows the last 5 page buttons if the navigation is close to the end
                        return (
                            <button
                                key={index}
                                className={paginationButtonWrapper}
                                onClick={() => {
                                    onChangePage(index);
                                }}
                                type="button"
                                data-tracklinktext={`pagination page ${index + 1} clicked`}
                            >{`${index + 1}`}</button>
                        );
                    } else if (index >= totalPages - 2) {
                        // Shows the last two page buttons if the shown page is not at the end
                        return (
                            <button
                                key={index}
                                className={paginationButtonWrapper}
                                onClick={() => {
                                    onChangePage(index);
                                }}
                                type="button"
                                data-tracklinktext={`pagination page ${index + 1} clicked`}
                            >
                                {`${index + 1}`}
                            </button>
                        );
                    }
                })}
            </div>
            <button
                onClick={() => {
                    const newValue = selectedPage + 1 !== totalPages ? selectedPage + 1 : totalPages - 1;

                    onChangePage(newValue);
                }}
                type="button"
                disabled={selectedPage === totalPages - 1}
                className={isMobileDevice ? rightArrowNoHoverStyles : rightArrowButtonStyles}
                data-tracklinktext="pagination arrow right"
                aria-label="Next page"
            >
                <IconSimpleArrow aria-hidden="true" focusable="false" />
            </button>
        </div>
    );
}
